/**
 * TemplatePicker.jsx — Phase 2 · step 1 (v200.86)
 *
 * The ONE unified, reusable Meta-template picker for the whole platform
 * (surveys + campaigns). It reads from the EXACT SAME single source that
 * feeds صندوق القوالب: `GET /whatsapp/templates/inbox`. No separate data
 * copy — whatever changes in the box (approve / reject / delete) is
 * reflected here on the next refresh (manual button + optional poll).
 *
 * ENFORCES approved-only selection by default (no free-form name entry).
 * Live variable substitution shows exactly what the recipient will see.
 *
 * NOT wired to any screen yet — mounted in isolation via TemplatePickerDemo.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import { pickDefaultAfterLoad } from "./lib/bulkWaHelpers";
import { InfoTip } from "./components/InfoTip";

const STATUS_META = {
  APPROVED: { label: "معتمد", bg: "#DCFCE7", color: "#166534", border: "#86EFAC", icon: "✅" },
  PENDING: { label: "بانتظار اعتماد Meta", bg: "#FEF3C7", color: "#92400E", border: "#FCD34D", icon: "⏳" },
  REJECTED: { label: "مرفوض", bg: "#FEE2E2", color: "#B91C1C", border: "#FCA5A5", icon: "❌" },
  PAUSED: { label: "موقوف", bg: "#F3E8FF", color: "#6B21A8", border: "#D8B4FE", icon: "⏸" },
};

// Body text lives directly on the inbox item (`body`), or inside a
// components[] array on the raw Meta shape — support both.
function bodyOf(t) {
  if (!t) return "";
  if (typeof t.body === "string" && t.body) return t.body;
  const bc = (t.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
  return bc?.text || "";
}

function extractVars(body) {
  if (!body) return [];
  const found = new Set();
  const re = /\{\{(\d+)\}\}/g;
  let m;
  while ((m = re.exec(body)) !== null) found.add(m[1]);
  return Array.from(found).sort((a, b) => Number(a) - Number(b));
}
// مُعاد التصدير من الوحدة النقية للاختبار (البند 6).
export { extractVars };

function substitute(body, values) {
  if (!body) return "";
  return body.replace(/\{\{(\d+)\}\}/g, (_m, n) => values[n] || `{{${n}}}`);
}

export default function TemplatePicker({
  value,                         // controlled: {name, variables}
  onChange,                      // (payload) => void
  onlyApproved = true,
  category = null,               // optional: filter by MARKETING/UTILITY/…
  source = "/whatsapp/templates/inbox",   // SINGLE source of truth = the box
  pollMs = 0,                    // >0 → auto-refresh from the same source
  onCreateNew = null,            // Phase 2 later step (wire the create flow)
  testidPrefix = "tpl",
  exactBodyVars = null,          // surveys pass 1: only templates whose BODY
                                 // has exactly this many {{n}} vars are
                                 // selectable (others disabled with a reason).
  allowedBodyVars = null,        // customer surveys pass [1,2]: templates with
                                 // exactly 1 var (link) OR 2 vars (product name
                                 // + link) are selectable. Supersedes exactBodyVars.
  varsAutoFilled = false,        // surveys: the link var is filled by the
                                 // backend per recipient — hide the manual var
                                 // inputs and treat as ready (product name, if
                                 // the template needs it, is entered on the
                                 // survey screen, not here).
  autoFillLabelFn = null,        // Part C: (n, idx, total) => preview label for
                                 // each auto-filled var. Default = survey labels.
  autoFillNote = null,           // Part C: override the green auto-fill note text.
  showDescriptions = true,       // Part C · Item 13: show + edit local Arabic desc.
  blockReasonFn = null,          // (vc) => reason string لعرض سبب تعطيل قالب مخالف
}) {
  const [state, setState] = useState({ loading: true, err: null, items: [], fetchedAt: null });
  // القيمة الابتدائية "" عمدًا — القالب الافتراضي (value.name) يُطبَّق *بعد* وصول
  // القائمة عبر التأثير أدناه، وفقط إن طابق خيارًا محمّلًا مسموحًا (لا اسم يضيع).
  const [selectedName, setSelectedName] = useState("");
  const [vars, setVars] = useState(value?.variables || {});
  const [showBlocked, setShowBlocked] = useState(false);   // v-nav-unify: غير المتاح مخفي افتراضيًا
  // PROD-FIX-3 (بند 4) — لا نُطلِق onChange قبل استقرار محاولة استرجاع
  // القالب الافتراضي (value.name). بدونها، onChange كان يُطلَق فورًا عند
  // mount بـname:null (قبل اكتمال جلب /whatsapp/templates/inbox)، وأي
  // مستهلك يحفظ عند كل onChange (كـLifecycleEnginePanel.jsx) كان يمحو
  // القالب المحفوظ فعليًا في تلك اللحظة. إن لم توجد قيمة افتراضية أصلاً
  // (value?.name فارغ) لا حاجة للانتظار — الاستقرار فوري.
  const [restoreSettled, setRestoreSettled] = useState(!value?.name);
  // Part C · Item 13 — local Arabic descriptions (tenant_settings, editable).
  const [descriptions, setDescriptions] = useState({});
  const [descDraft, setDescDraft] = useState("");
  const [descBusy, setDescBusy] = useState(false);
  const [descMsg, setDescMsg] = useState(null);

  const loadDescriptions = useCallback(async () => {
    if (!showDescriptions) return;
    try {
      const r = await api.get("/whatsapp/template-descriptions");
      setDescriptions(r.data?.descriptions || {});
    } catch {
      setDescriptions({});
    }
  }, [showDescriptions]);
  useEffect(() => { loadDescriptions(); }, [loadDescriptions]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(source);
      const raw = r.data?.items || r.data?.data || r.data?.templates
               || (Array.isArray(r.data) ? r.data : []);
      let items = raw.map((t) => ({
        name: t.name,
        status: String(t.status || "").toUpperCase(),
        category: t.category || null,
        language: t.language || null,
        body: bodyOf(t),
        header_format: t.header_format || null,
        header_sample_url: t.header_sample_url || null,
        // 2026-10-07 (بند 1) — تصنيف الجمهور (customer/internal/غير مصنَّف):
        // selectable=false تُعطِّل القالب بسببه الصريح، لا تحذفه من القائمة.
        // t.selectable قد يكون غائبًا من مصادر أقدم ⇒ افتراضيًا متاح (true).
        selectable: t.selectable !== false,
        disabled_reason_ar: t.disabled_reason_ar || null,
        classify_link: t.classify_link || null,
      }));
      if (onlyApproved) items = items.filter((t) => t.status === "APPROVED");
      if (category) items = items.filter((t) => (t.category || "").toUpperCase() === category.toUpperCase());
      setState({ loading: false, err: null, items, fetchedAt: new Date() });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error || e?.message || "خطأ";
      setState({ loading: false, err: msg, items: [], fetchedAt: new Date() });
    }
  }, [source, onlyApproved, category]);

  useEffect(() => {
    load();
    if (pollMs > 0) {
      const id = setInterval(load, pollMs);
      return () => clearInterval(id);
    }
  }, [load, pollMs]);

  // البند 2 — حمّل القالب الافتراضي (value.name) *بعد* وصول القائمة لا قبلها:
  // يُطبَّق فقط إن كان موجودًا ضمن القوالب المحمّلة والمسموحة (بوابة المتغيّرات)،
  // وإن لم يكن شيء مختارًا بعد. للتصنيفات بلا افتراضي يبقى "" (زر الإرسال معطّل).
  useEffect(() => {
    if (state.loading || selectedName || !value?.name) return;
    const allowedNames = state.items
      .filter((t) => isAllowed(t))
      .map((t) => t.name);
    const pick = pickDefaultAfterLoad(value.name, allowedNames);
    if (pick) setSelectedName(pick);
    setRestoreSettled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loading, state.items, value?.name]);

  const selected = useMemo(
    () => state.items.find((t) => t.name === selectedName) || null,
    [state.items, selectedName],
  );

  const allowedVars = allowedBodyVars != null
    ? allowedBodyVars
    : (exactBodyVars != null ? [exactBodyVars] : null);
  const isVarCountAllowed = (vc) => allowedVars == null || allowedVars.includes(vc);
  // 2026-10-07 (بند 1) — «متاح» يتطلب الآن شرطين: عدد متغيرات مسموح لهذا
  // السياق + تصنيف جمهور يسمح بالإرسال للعملاء (selectable). لا إخفاء
  // صامت — القالب غير المصنَّف يبقى في القائمة معطَّلًا بسببه.
  const isAllowed = (t) => isVarCountAllowed(extractVars(bodyOf(t)).length) && t.selectable !== false;
  const blockedCount = state.items.filter((t) => !isAllowed(t)).length;

  const body = bodyOf(selected);
  const varsRequired = extractVars(body);
  const autoPreview = (() => {
    if (!varsAutoFilled) return vars;
    const map = {};
    varsRequired.forEach((n, idx) => {
      map[n] = autoFillLabelFn
        ? autoFillLabelFn(n, idx, varsRequired.length)
        : (idx === varsRequired.length - 1 ? "«رابط الاستبيان»" : "«اسم المنتج»");
    });
    return map;
  })();
  const filledText = substitute(body, varsAutoFilled ? autoPreview : vars);
  const allFilled = varsRequired.every((k) => (vars[k] || "").trim().length > 0);
  const isReady = !!selected && (varsAutoFilled || allFilled);

  // Guard: if a preselected/persisted template violates the survey var
  // constraint (≠ exactBodyVars), clear it so no unsendable template sticks.
  useEffect(() => {
    if (selected && !isAllowed(selected)) {
      setSelectedName("");
      setVars({});
    }
  }, [selected, exactBodyVars, allowedBodyVars]);

  useEffect(() => {
    if (typeof onChange !== "function") return;
    // PROD-FIX-3 (بند 4) — انتظر استقرار محاولة استرجاع الافتراضي أولاً.
    if (!restoreSettled) return;
    onChange({
      name: selectedName || null,
      language: selected?.language || null,
      category: selected?.category || null,
      status: selected?.status || null,
      body,
      variables: vars,
      vars_required: varsRequired,
      filled_text: filledText,
      is_ready: isReady,
      // المرحلة 1 (رأس صورة) — حقول إضافية؛ null للقوالب النصية (لا أثر عليها).
      header_format: selected?.header_format || null,
      header_sample_url: selected?.header_sample_url || null,
    });
  }, [selectedName, body, filledText, isReady, restoreSettled]); // eslint-disable-line

  const setVar = (k, v) => setVars((s) => ({ ...s, [k]: v }));
  const stMeta = STATUS_META[selected?.status] || null;

  // Part C · Item 13 — sync the editable draft with the selected template.
  useEffect(() => {
    setDescDraft(descriptions[selectedName] || "");
    setDescMsg(null);
  }, [selectedName, descriptions]);

  const saveDescription = async () => {
    if (!selectedName) return;
    setDescBusy(true); setDescMsg(null);
    try {
      await api.put("/whatsapp/template-descriptions", { name: selectedName, description: descDraft });
      setDescriptions((s) => ({ ...s, [selectedName]: descDraft }));
      setDescMsg({ ok: true, text: "✅ حُفظ الوصف العربي" });
    } catch (e) {
      setDescMsg({ ok: false, text: e?.response?.data?.detail || "تعذّر حفظ الوصف" });
    } finally { setDescBusy(false); }
  };

  return (
    <section dir="rtl" data-testid={`${testidPrefix}-root`} style={{
      display: "grid", gap: 12, padding: "16px 18px", background: "#FFFFFF",
      border: "1px solid rgba(170,149,102,0.35)", borderRadius: 14,
      fontFamily: "'El Messiri', -apple-system, sans-serif",
    }}>
      <header style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#3D2A1C" }}>📄 اختيار قالب معتمد</div>
        <div style={{ fontSize: 11, color: "#78716C" }}>
          {onlyApproved ? "معتمد فقط · من صندوق القوالب (مصدر واحد)" : "جميع الحالات"}
        </div>
        <button data-testid={`${testidPrefix}-refresh`} onClick={load} className="btn btn-ghost"
          style={{ marginInlineStart: "auto", fontSize: 12 }}>↻ تحديث من المصدر</button>
      </header>

      {state.loading && (
        <div data-testid={`${testidPrefix}-loading`} style={{ padding: 20, textAlign: "center", color: "#78716C", fontSize: 13 }}>
          جارٍ التحميل من صندوق القوالب…
        </div>
      )}

      {!state.loading && state.err && (
        <div data-testid={`${testidPrefix}-error`} style={{ padding: 12, borderRadius: 10, background: "#FEE2E2", color: "#B91C1C", border: "1px solid #FCA5A5", fontSize: 12, lineHeight: 1.7 }}>
          تعذّر جلب القوالب من المصدر: {state.err}
          <div style={{ fontSize: 11, marginTop: 4, color: "#92400E" }}>
            (على Preview لا مفاتيح WhatsApp ⇒ المصدر يعود فارغًا — تظهر القوالب الحقيقية على الإنتاج فقط.)
          </div>
        </div>
      )}

      {!state.loading && !state.err && state.items.length === 0 && (
        <div data-testid={`${testidPrefix}-empty`} style={{ padding: 16, borderRadius: 10, background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A", fontSize: 12, lineHeight: 1.7 }}>
          🚫 لا توجد قوالب معتمدة في المصدر حاليًا.
        </div>
      )}

      {!state.loading && state.items.length > 0 && (
        <>
          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#57534E" }}>
              القالب ({state.items.filter((t) => isAllowed(t)).length} متاح من {state.items.length} معتمد)
            </span>
            <select data-testid={`${testidPrefix}-select`} value={selectedName}
              onChange={(e) => { setSelectedName(e.target.value); setVars({}); }}
              style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(61,42,28,0.14)", fontSize: 13, background: "#F7F2EC", color: "#3D2A1C" }}>
              <option value="">— اختر قالبًا —</option>
              {state.items.map((t) => {
                const vc = extractVars(bodyOf(t)).length;
                const vtag = vc === 0 ? "🔒 ثابت" : vc === 1 ? "🔤 متغير" : `🎟 ${vc} متغيرات`;
                const blocked = !isAllowed(t);
                if (blocked && !showBlocked) return null;   // مخفي افتراضيًا — يُكشف عند الطلب
                const desc = showDescriptions ? (descriptions[t.name] || "") : "";
                const classGated = t.selectable === false;
                const blockMsg = classGated
                  ? ` — 🚫 ${t.disabled_reason_ar || "غير مصنّف للعملاء"}`
                  : (blockReasonFn
                  ? blockReasonFn(vc)
                  : (allowedVars && allowedVars.length > 1
                  ? " — 🚫 غير متاح (يتطلب متغيّرًا واحدًا = رابط، أو متغيّرين = اسم منتج + رابط)"
                  : " — 🚫 غير متاح (يتطلب متغيّرًا واحدًا فقط)"));
                return <option key={`${t.name}::${t.language || ""}`} value={t.name} disabled={blocked} data-testid={`${testidPrefix}-opt-${t.name}`}>{t.name} · {t.language} · {t.category} · {vtag}{desc ? ` — ${desc}` : ""}{blocked ? blockMsg : ""}</option>;
              })}
            </select>
            {state.items.some((t) => t.selectable === false) && (
              <div data-testid={`${testidPrefix}-classify-hint`} style={{ fontSize: 11, color: "#92400E" }}>
                ⚠ بعض القوالب المعتمدة غير مصنَّفة للعملاء —{" "}
                <a href="/?tab=marketing_hub&sub=wa-cls" target="_blank" rel="noreferrer"
                   data-testid={`${testidPrefix}-classify-link`} style={{ color: "#166534", fontWeight: 700, textDecoration: "underline" }}>
                  صنّفها من هنا
                </a>
              </div>
            )}
            {blockedCount > 0 && (
              <button type="button" onClick={() => setShowBlocked((s) => !s)}
                      data-testid={`${testidPrefix}-toggle-blocked`}
                      style={{ alignSelf: "flex-start", border: "none", background: "transparent", color: "#92400E", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "2px 0", fontFamily: "inherit" }}>
                {showBlocked ? `▲ إخفاء غير المتاح` : `▾ عرض ${blockedCount} قالبًا غير متاح`}
              </button>
            )}
            {allowedVars != null && (
              <span data-testid={`${testidPrefix}-var-constraint`} style={{ fontSize: 11, color: "#92400E", lineHeight: 1.7 }}>
                {allowedVars.length > 1
                  ? "ℹ️ الاستبيانات تدعم قالبًا بمتغيّر واحد = رابط الاستبيان (يُملأ تلقائيًا)، أو بمتغيّرين = {{1}} اسم المنتج (تُدخله بالأسفل على شاشة الاستبيان) + {{2}} رابط الاستبيان (يُملأ تلقائيًا). أي عدد آخر من المتغيّرات معطّل هنا لأن إرساله سيفشل."
                  : "ℹ️ الاستبيانات تدعم قالبًا بمتغيّر واحد فقط = رابط الاستبيان (يُملأ تلقائيًا لكل مستلم). القوالب بلا متغيّر أو بأكثر من متغيّر معطّلة هنا لأن إرسالها سيفشل."}
              </span>
            )}
          </label>

          {selected && (
            <>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {stMeta && (
                  <span data-testid={`${testidPrefix}-status`} style={{ padding: "3px 10px", borderRadius: 999, background: stMeta.bg, color: stMeta.color, border: `1px solid ${stMeta.border}`, fontSize: 11, fontWeight: 700 }}>
                    {stMeta.icon} {stMeta.label}
                  </span>
                )}
                <span style={{ padding: "3px 10px", borderRadius: 999, background: "rgba(170,149,102,0.14)", color: "#78716C", fontSize: 11, fontWeight: 600 }}>الفئة: {selected.category || "—"}</span>
                <span style={{ padding: "3px 10px", borderRadius: 999, background: varsRequired.length ? "rgba(2,132,199,0.12)" : "rgba(107,33,168,0.12)", color: varsRequired.length ? "#0284C7" : "#6B21A8", fontSize: 11, fontWeight: 700 }}>
                  {varsRequired.length ? `${varsRequired.length} متغيرات مطلوبة` : "بدون متغيرات"}
                </span>
              </div>

              {varsRequired.length > 0 && !varsAutoFilled && (
                <div data-testid={`${testidPrefix}-vars`} style={{ display: "grid", gap: 8, padding: 12, background: "#F7F2EC", borderRadius: 10 }}>
                  {varsRequired.map((n) => (
                    <label key={n} style={{ display: "grid", gap: 4 }}>
                      <span style={{ fontSize: 11, color: "#57534E" }}>{`{{${n}}}`}</span>
                      <input type="text" data-testid={`${testidPrefix}-var-${n}`} value={vars[n] || ""} onChange={(e) => setVar(n, e.target.value)}
                        style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(61,42,28,0.16)", fontSize: 13, background: "#FFFFFF", fontFamily: "inherit" }} />
                    </label>
                  ))}
                </div>
              )}

              {varsRequired.length > 0 && varsAutoFilled && (
                <div data-testid={`${testidPrefix}-autofill-note`} style={{ padding: 10, background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 10, fontSize: 11.5, color: "#166534", lineHeight: 1.7 }}>
                  {autoFillNote
                    ? autoFillNote
                    : (varsRequired.length === 1
                      ? "🔗 المتغيّر {{1}} = رابط الاستبيان، يُدرَج تلقائيًا لكل مستلم عند الإرسال — لا حاجة لتعبئته هنا."
                      : "🔗 المتغيّر {{1}} = اسم المنتج (أدخله في حقل «اسم المنتج» على شاشة الاستبيان)، والمتغيّر {{2}} = رابط الاستبيان (يُدرَج تلقائيًا لكل مستلم عند الإرسال).")}
                </div>
              )}

              {showDescriptions && (
                <div data-testid={`${testidPrefix}-desc-editor`} style={{ display: "grid", gap: 6, padding: 12, background: "#FBF7F1", border: "1px solid rgba(170,149,102,0.3)", borderRadius: 10 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: "#57534E" }}>
                    📝 وصف عربي للقالب (محلي — لا يُرسَل لـMeta · قابل للتحرير)
                  </span>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <input type="text" data-testid={`${testidPrefix}-desc-input`} value={descDraft}
                      onChange={(e) => setDescDraft(e.target.value)} maxLength={280}
                      placeholder="مثال: الزوار المتوقعون — رسالة من فاتهم موعدهم"
                      style={{ flex: "1 1 240px", padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(61,42,28,0.16)", fontSize: 13, background: "#FFFFFF", fontFamily: "inherit" }} />
                    <button type="button" data-testid={`${testidPrefix}-desc-save`} onClick={saveDescription}
                      disabled={descBusy} className="btn btn-primary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {descBusy ? "⏳ حفظ…" : "💾 حفظ الوصف"}
                    </button>
                  </div>
                  {descMsg && (
                    <span data-testid={`${testidPrefix}-desc-msg`} style={{ fontSize: 11, color: descMsg.ok ? "#166534" : "#B91C1C" }}>
                      {descMsg.text}
                    </span>
                  )}
                </div>
              )}

              {selected?.header_format === "IMAGE" && (
                <div data-testid={`${testidPrefix}-image-header`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, background: "#EFF6FF", border: "1px solid #BFDBFE", marginBottom: 8 }}>
                  {selected?.header_sample_url ? (
                    <img data-testid={`${testidPrefix}-image-thumb`} src={selected.header_sample_url} alt="header"
                      style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8, border: "1px solid #BFDBFE" }} />
                  ) : (
                    <span style={{ fontSize: 22 }}>🖼️</span>
                  )}
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: "#1D4ED8" }} data-testid={`${testidPrefix}-image-badge`}>
                    قالب مصوَّر — يحمل رأس صورة
                  </span>
                </div>
              )}

              <div data-testid={`${testidPrefix}-preview`} style={{ padding: "12px 14px", borderRadius: 12, background: "#25D3661A", border: "1px solid rgba(37,211,102,0.35)", fontSize: 13, color: "#0F2A1D", lineHeight: 1.9, whiteSpace: "pre-wrap" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#166534", marginBottom: 6 }}>💬 معاينة الرسالة كما سيراها العميل:</div>
                {filledText || <span style={{ color: "#A8A29E" }}>—</span>}
              </div>

              <div data-testid={`${testidPrefix}-ready`} data-ready={isReady ? "1" : "0"} style={{ fontSize: 11, color: isReady ? "#166534" : "#78716C", fontWeight: 700 }}>
                {isReady ? "✅ جاهز — يمكن الإرسال (مع قفل المالك)" : "🚫 غير جاهز — أكمل المتغيرات"}
              </div>
            </>
          )}
        </>
      )}

      <div style={{ borderTop: "1px dashed rgba(61,42,28,0.14)", paddingTop: 10 }}>
        <button data-testid={`${testidPrefix}-create-new`} className="btn btn-outline" style={{ fontSize: 12 }}
          onClick={() => (typeof onCreateNew === "function" ? onCreateNew() : null)}
          disabled={typeof onCreateNew !== "function"}
  aria-label={typeof onCreateNew === "function" ? "" : "يُفعّل في خطوة لاحقة من المرحلة 2"}>
          ＋ إنشاء قالب جديد (يُرسَل لاعتماد Meta)
        </button>
      </div>
    </section>
  );
}
